import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FormGenerator } from '../helper/FormGenerator';
import { PageList, FormLayout, FormLayout2 } from '../components/PageComponents';
import { usePageAPI } from '../hooks/usePageAPI';
import Head from './layouts/Head';
import LoadingDashboardContent from '../components/LoadingDashboardContent';
import { API_URL, APP_URL, absoluteUrl } from '../constants';
import axios from 'axios';
import TagInput from '../components/TagInput';
import { idFormat } from '../utils/appUtils';
import { useTokens } from '../context/TokenContext';
import SeoPreview from '../components/SeoPreview';

const AdminPost = () => {
  const { getItem } = useTokens();
  const adminAuthToken = getItem('admin', 'AuthToken');
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { fetchPages, fetchPage, createPage, updatePage, deletePage } = usePageAPI();
  const [formData, setFormData] = useState({});
  const [pageData, setPageData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [searchBy, setSearchBy] = useState('title');
  const [searchValue, setSearchValue] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);
  const [onInit, setOnInit] = useState({});
  const [isReload, setIsReload] = useState(false);

  const pageType = {
    isNew: pathname.includes('new'),
    isEdit: pathname.includes('edit'),
    isList: !(pathname.includes('new') || pathname.includes('edit')),
  };

  useEffect(() => {
    const params = new URLSearchParams(search);
    const page = Number(params.get('page')) || 1;
    const limit = Number(params.get('limit')) || 20;
    const searchBy = params.get('searchBy') || 'title';
    const searchValue = params.get('searchValue') || '';

    setErrorMessage(null);
    setCurrentPage(page);
    setLimit(limit);
    setSearchBy(searchBy);
    setSearchValue(searchValue);

    // Reset form data when adding a new post
    if (pageType.isNew) {
      setFormData({tags: []});
    }

    const loadData = () => {
      setLoading(true);
      axios.get(`${API_URL}/content`, {
        params: {
          postType: 'post',
          limit,
          currentPage: page,
          searchBy,
          searchValue,
        },
        headers: {
          'Authorization': `Bearer ${adminAuthToken}`,
        },
      })
        .then((response) => {
          setPageData({ data: response.data.data, dataCount: response.data.dataCount });
        })
        .catch(error => {
          console.error('Failed to fetch data:', error);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    if (pageType.isList) {
      loadData();
    }

    if (pageType.isEdit) {
      const pageId = pathname.split('/').pop();

      const loadPageData = () => {
        setLoading(true);
        axios.get(`${API_URL}/content/${pageId}/post`)
          .then(response => {
            if (response.data.success) {
              setFormData(response.data.content);
            } else {
              setErrorMessage(response.data.message || 'Error fetching content');
            }
          })
          .catch(error => {
            console.error('Error fetching content:', error);
            setErrorMessage('Failed to fetch content');
          })
          .finally(() => {
            setLoading(false);
          });
      };

      loadPageData();
    }

    // Fetch onload data
    if (pageType.isNew || pageType.isEdit) {
      const initData = axios
        .get(`${API_URL}/content/onload`, {
          headers: {
            'Authorization': `Bearer ${adminAuthToken}`,
          }
        });

      // Handling the promise with then
      initData.then((response) => {
        if (response.data.success) {
          setOnInit(response.data.data);
          setLoading(false);
        } else {
          console.error(response.data.message);
          setErrorMessage(response.data.message);
        }
      });
    }

    if (isReload) {
      setIsReload(false);
    }
  }, [pathname, search, isReload]);

  const mapToObject = (arr) => {
    if (Array.isArray(arr) && arr.length > 0) {
      return arr.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {});
    }
    return { '': 'No data found' };
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const formDataToSubmit = {
      ...formData,
      featureImage: formData.featureImage,
      title: formData.title,
      description: formData.content,
      slug: formData.slug,
      publishType: formData.publishType,
      postType: formData.postType,
      seoTitle: formData.seoTitle,
      seoDescription: formData.seoDescription,
      postType: 'post',
    };

    const toastId = toast.loading(`Post ${pageType.isEdit ? 'updating ...' : 'creating ...'}`, { autoClose: false });

    try {
      const pageId = pageType.isEdit ? pathname.split('/').pop() : null;
      const result = pageType.isEdit
        ? await updatePage({ type: 'content', pageId, formDataToSubmit })
        : await createPage({ type: 'content', formDataToSubmit });

      if (result) {
        toast.update(toastId, {
          render: `Post ${pageType.isEdit ? 'updated' : 'created'} successfully!`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });

        if (pageType.isEdit) {
          setIsReload(true);
        }

        navigate(`/admin/post/edit/${pageType.isEdit ? pageId : result.data.data.id}`);
      } else {
        toast.update(toastId, {
          render: result.error,
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.error || 'An unknown error occurred';
      toast.update(toastId, {
        render: errorMessage,
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      console.error('Error occurred:', error);
    } finally {
    }
  };

  const handlePageDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this page?")) {
      const toastId = toast.loading('Deleting ...', { autoClose: false });
      const success = await deletePage({ type: 'content', id, setLoading });

      if (success) {
        setPageData((prev) => ({
          ...prev,
          data: prev.data.filter((page) => page.id !== id),
        }));

        toast.update(toastId, {
          render: `Post deleted successfully!`,
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      } else {
        toast.update(toastId, {
          render: 'Failed to delete page',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      }
    }
  };

  const updateUrlParams = (page, limit, searchBy, searchValue) => {
    const params = new URLSearchParams();
    params.set('page', page);
    params.set('limit', limit);
    params.set('searchBy', searchBy);
    params.set('searchValue', searchValue);
    navigate({ pathname: '/admin/post/list', search: params.toString() });
  };

  if (errorMessage) {
    return (
      <div style={{ color: 'red' }}>
        <p>{errorMessage}</p>
      </div>
    );
  }

  const getTagOptions = (tags) => {
    if (!Array.isArray(tags)) return [];

    const flatTags = tags.flat().filter(tag => typeof tag === 'string');

    return flatTags.map(tag => ({ value: tag }));
  };

  return (
    <>
      <Head title={getPageTitle(pageType)} />

      {pageType.isNew || pageType.isEdit ? (
        loading ? <LoadingDashboardContent progress={progress} /> :
          <FormLayout2
            title={pageType.isEdit ? "Edit Post" : "Create New Post"}
            submitText={pageType.isEdit ? (loading ? 'Updating ...' : 'Update') : (loading ? 'Creating ...' : 'Create')}
            onCancel={() => navigate('/admin/post')}
            onSubmit={handleFormSubmit}
          >
            <div>
              <div className='border p-4 bg-white'>
                <FormGenerator
                  fields={[
                    { id: 'title', label: 'Post Title', type: 'text', desc: 'Enter Post Title', required: true },
                    { id: 'content', label: 'Post Content', type: 'texteditor', desc: 'Enter Post Title', required: true },
                  ]}
                  fieldData={formData}
                  onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
                  authToken={adminAuthToken}
                />
              </div>
            </div>
            <div>
              <div className='border p-4 bg-white'>
                <FormGenerator
                  fields={[
                    { id: 'publishType', label: 'Status', type: 'select', options: { 'published': 'Published', 'draft': 'Draft' }, required: true },
                    { id: 'featureImage', label: 'Feature Image', type: "upload_single" },
                    { id: 'category', label: 'Product Category', type: 'select', options: mapToObject(onInit.category), searchable: true, required: true },
                  ]}
                  fieldData={formData}
                  onChange={(field, value) => setFormData(prev => ({ ...prev, [field]: value }))}
                  authToken={adminAuthToken}
                />

                <TagInput
                  defaultValue={formData.tags}
                  predefinedTags={onInit.tags}
                  setFormData={setFormData}
                  label={'Post Tags'}
                  type={'tags'}
                />
              </div>

              {pageType.isEdit ?
                <div className='border p-4 bg-white mt-4'>
                  <SeoPreview
                    initialData={{
                      slug: formData.slug,
                      metaTitle: formData.title,
                      metaDescription: formData.metaDescription,
                      structure: ''
                    }}
                    onChange={(updatedData) => setFormData((prevFormData) => ({
                      ...prevFormData,
                      slug: updatedData.slug,
                      metaDescription: updatedData.metaDescription,
                    }))}
                  />
                </div> : ''
              }
            </div>
          </FormLayout2>
      ) : null}

      {pageType.isList && (
        loading ? <LoadingDashboardContent progress={progress} /> :
          <PageList
            itemType={'pages'}
            itemId={'id'}
            pageTitle={'Post List'}
            pageData={pageData.data}
            actionApplyKeyMap={{
              'Published': 'published',
              'Draft': 'draft',
              'Delete': 'delete',
            }}
            columns={['Id', 'Title', 'Categories', 'State']}
            filterBy={{
              '--select--': '',
              'Post Id': 'id',
              'Post Title': 'title'
            }}
            columnKeyMap={{
              Id: item => idFormat(item.id),
              'Title': (item) => (<><Link className='a_edit ms-2' to={`/admin/post/edit/${item.id}`}>{item.title}</Link><a className='a_view ms-2' href={absoluteUrl(`/${item.slug}`)}><i className='bi bi-eye'></i></a></>),
              'Categories': (item) => {
                let categories = [];

                if (Array.isArray(item.postCategory)) {
                  categories = item.postCategory;
                } else if (typeof item.postCategory === 'string') {
                  try {
                    const parsed = JSON.parse(item.postCategory);
                    if (Array.isArray(parsed)) {
                      categories = parsed;
                    }
                  } catch {
                    // not a valid JSON string, leave categories empty
                  }
                }

                return (
                  <>
                    {categories.map((category, idx) => (
                      <span key={idx} className="badge bg-info text-dark me-1">
                        {category}
                      </span>
                    ))}
                  </>
                );
              },
              'State': (item) =>
                item.publishType == 'published' ? (
                  <span className="badge bg-info text-bg-secondary">Published</span>
                ) : item.publishType == 'draft' ? (
                  <span className="badge text-bg-danger">Draft</span>
                ) : null,
            }}
            onAddNew={() => navigate('/admin/post/new')}
            onView={(id) => navigate(`/admin/post/view/${id}`)}
            onEdit={(id) => navigate(`/admin/post/edit/${id}`)}
            onDelete={handlePageDelete}
            totalRows={pageData.dataCount}
            updateUrlParams={updateUrlParams}
            isReload={setIsReload}
          />
      )}
    </>
  );
};

const getPageTitle = ({ isEdit, isNew }) =>
  isEdit ? "Edit Post" : isNew ? "Create New Post" : "Post List";
export default AdminPost;
